import { assertCustomerDomainAllowed, type IdentityContext, type PlatformRepository, type SaveOrganizationDomainInput, type SavePlatformOrganizationInput } from '@zetyar/data-contracts'
import type { UUID } from '@zetyar/domain'
import type { SupabaseClient } from '@supabase/supabase-js'

function verificationToken(): string {
  return `zetyar-domain-${crypto.randomUUID()}`
}

export function createSupabasePlatformRepository(client: SupabaseClient): PlatformRepository {
  return {
    async listAllOrganizations(_context: IdentityContext, limit: number) {
      const { data, error } = await client
        .from('organizations')
        .select(`
          id, name, slug, legal_name, logo_url, logo_dark_url, favicon_url, brand_color, primary_domain,
          status, timezone, locale, created_at, updated_at,
          memberships (id, status),
          organization_domains (id, domain, is_primary, status, verification_token, verified_at, created_at, updated_at, deleted_at)
        `)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(Math.max(1, Math.min(limit, 500)))

      if (error) throw error

      return (data ?? []).map((item) => {
        const memberships = (item.memberships as unknown as { status: string }[]) ?? []
        const domains = (item.organization_domains as unknown as {
          id: UUID; domain: string; is_primary: boolean; status: string; verification_token: string
          verified_at: string | null; created_at: string; updated_at: string; deleted_at: string | null
        }[]) ?? []

        return {
          id: item.id as UUID,
          name: item.name as string,
          slug: item.slug as string,
          legalName: (item.legal_name as string | null) ?? null,
          logoUrl: (item.logo_url as string | null) ?? null,
          logoDarkUrl: (item.logo_dark_url as string | null) ?? null,
          faviconUrl: (item.favicon_url as string | null) ?? null,
          brandColor: (item.brand_color as string | null) ?? null,
          primaryDomain: (item.primary_domain as string | null) ?? null,
          domains: domains
            .filter((d) => d.deleted_at === null)
            .map((d) => ({
              id: d.id,
              domain: d.domain,
              isPrimary: d.is_primary,
              status: d.status as 'pending' | 'active' | 'rejected' | 'disabled',
              verificationToken: d.verification_token,
              verifiedAt: d.verified_at,
              createdAt: d.created_at,
              updatedAt: d.updated_at,
            })),
          status: item.status as string,
          timezone: item.timezone as string,
          locale: item.locale as string,
          createdAt: item.created_at as string,
          updatedAt: item.updated_at as string,
          activeMemberCount: memberships.filter((m) => m.status === 'active').length,
        }
      })
    },

    async createOrganization(context: IdentityContext, input: SavePlatformOrganizationInput) {
      const { error } = await client.from('organizations').insert({
        name: input.name,
        slug: input.slug,
        legal_name: input.legalName ?? null,
        logo_url: input.logoUrl ?? null,
        logo_dark_url: input.logoDarkUrl ?? null,
        favicon_url: input.faviconUrl ?? null,
        brand_color: input.brandColor ?? null,
        primary_domain: input.primaryDomain ? assertCustomerDomainAllowed(input.primaryDomain) : null,
        status: input.status,
        timezone: input.timezone,
        locale: input.locale,
        created_by: context.personId,
      })
      if (error) throw error
    },

    async updateOrganization(_context: IdentityContext, id: UUID, input: SavePlatformOrganizationInput) {
      const { error } = await client
        .from('organizations')
        .update({
          name: input.name,
          slug: input.slug,
          legal_name: input.legalName ?? null,
          logo_url: input.logoUrl ?? null,
          logo_dark_url: input.logoDarkUrl ?? null,
          favicon_url: input.faviconUrl ?? null,
          brand_color: input.brandColor ?? null,
          primary_domain: input.primaryDomain ? assertCustomerDomainAllowed(input.primaryDomain) : null,
          status: input.status,
          timezone: input.timezone,
          locale: input.locale,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
      if (error) throw error
    },

    async createDomain(context: IdentityContext, organizationId: UUID, input: SaveOrganizationDomainInput) {
      const { error } = await client.from('organization_domains').insert({
        organization_id: organizationId,
        domain: assertCustomerDomainAllowed(input.domain),
        is_primary: input.isPrimary,
        status: 'pending',
        verification_token: verificationToken(),
        created_by: context.personId,
      })
      if (error) throw error
    },

    async verifyDomain(_context: IdentityContext, organizationId: UUID, domainId: UUID, isPrimary: boolean) {
      const { error } = await client
        .from('organization_domains')
        .update({ status: 'active', is_primary: isPrimary, verified_at: new Date().toISOString(), updated_at: new Date().toISOString() })
        .eq('id', domainId)
        .eq('organization_id', organizationId)
      if (error) throw error
    },

    async disableDomain(_context: IdentityContext, organizationId: UUID, domainId: UUID) {
      const { error } = await client
        .from('organization_domains')
        .update({ status: 'disabled', is_primary: false, updated_at: new Date().toISOString(), deleted_at: new Date().toISOString() })
        .eq('id', domainId)
        .eq('organization_id', organizationId)
      if (error) throw error
    },

    async getMetrics(_context: IdentityContext) {
      const [orgCount, personCount] = await Promise.all([
        client.from('organizations').select('id', { count: 'exact', head: true }).is('deleted_at', null),
        client.from('persons').select('id', { count: 'exact', head: true }),
      ])

      if (orgCount.error) throw orgCount.error
      if (personCount.error) throw personCount.error

      return { organizationCount: orgCount.count ?? 0, personCount: personCount.count ?? 0 }
    },
  }
}
